import type { PropertiesNullable } from '@@types/helpers'
import { LicenseStatus } from '@app/stores/StoreLicense/types'
import { parseDateSafe } from '@libs/dates/helpers'
import type {
  License,
  LicenseFeature,
  LicenseType,
  Maybe
} from '@server/graphql/typeDefs/types'
import moment from 'moment'
import EntityBase from './EntityBase'

export default class EntityLicense
  extends EntityBase
  implements PropertiesNullable<License>
{
  customerName: Maybe<string> = null
  type: Maybe<LicenseType> = null
  currentActiveUserCount: Maybe<number> = null
  maxActiveUserCount: Maybe<number> = null
  features: Maybe<LicenseFeature[]> = null
  expirationDateUTC: Maybe<string> = null
  inAppEula: Maybe<boolean> = null
  activationCode: Maybe<string> = null
  containerUuid: Maybe<string> = null
  productAssociation: Maybe<string> = null
  maxUsersReachedStartingDate: Maybe<string> = null
  daysCountSinceMaxUsersReached: Maybe<number> = null
  gracePeriod = 45

  constructor(data: Partial<License>) {
    super()
    Object.assign(this, data)
  }

  /**
   * Return the status of the license.
   */
  getStatus(): LicenseStatus {
    if (!this.type) {
      return LicenseStatus.unset
    }

    /**
     * Check the expiration date.
     */

    const expirationDateUTC = this.getPropertyAsString('expirationDateUTC')

    if (!expirationDateUTC) {
      return LicenseStatus.expired
    }

    if (moment.utc().isAfter(moment.utc(expirationDateUTC))) {
      return LicenseStatus.expired
    }

    /**
     * Check the nb max of users allowed.
     */

    // License is "over violated" (more than 10% overusage)
    if (
      this.getPropertyAsNumber('currentActiveUserCount') >
      this.getPropertyAsNumber('maxActiveUserCount') * 1.1
    ) {
      const maxUsersReachedStartingDate = parseDateSafe(
        this.getPropertyAsString('maxUsersReachedStartingDate')
      )

      if (maxUsersReachedStartingDate) {
        this.daysCountSinceMaxUsersReached =
          this._calculateDaysCountSinceMaxUsersReached(
            maxUsersReachedStartingDate
          )

        this.gracePeriod = this.getPropertyAsNumber('gracePeriod')

        if (this.daysCountSinceMaxUsersReached > this.gracePeriod) {
          return LicenseStatus.overViolatedGracePeriodOver
        }
      }

      return LicenseStatus.overViolatedUnderGracePeriod
    }

    // License is violated in an acceptable amount (less than 10% overusage)
    if (
      this.getPropertyAsNumber('currentActiveUserCount') >
      this.getPropertyAsNumber('maxActiveUserCount')
    ) {
      return LicenseStatus.violated
    }

    // Close to license violation
    if (
      this.getPropertyAsNumber('currentActiveUserCount') >
      this.getPropertyAsNumber('maxActiveUserCount') * 0.95
    ) {
      return LicenseStatus.nearViolated
    }

    return LicenseStatus.valid
  }

  /**
   * Private
   */

  private _calculateDaysCountSinceMaxUsersReached(
    maxUsersReachedStartingDate: Date
  ): number {
    const maxUsersReachedDate = new Date(maxUsersReachedStartingDate)
    const today = new Date()

    // Normalize both dates to midnight UTC
    maxUsersReachedDate.setUTCHours(0, 0, 0, 0)
    today.setUTCHours(0, 0, 0, 0)

    // Convert to timestamps and calculate difference
    const diffInMs = today.getTime() - maxUsersReachedDate.getTime()

    return Math.floor(diffInMs / (1000 * 60 * 60 * 24))
  }
}
